import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import { API_BASE_URL } from '../config/ApiConfig';
import DemandaModal from './components/modals/DemandaModal';

const MONTHS_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DIAS_PT = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

function Home() {
  const [demandas, setDemandas] = useState([]);
  const [mesAtual, setMesAtual] = useState(new Date().getMonth());
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [demandasDoDia, setDemandasDoDia] = useState({ criadas: [], prazos: [] });
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const [showDemandaModal, setShowDemandaModal] = useState(false);
  const [demandaVisualizar, setDemandaVisualizar] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const token = localStorage.getItem('token');
  const usuario = JSON.parse(localStorage.getItem('user') || '{}');
  const vinculo = JSON.parse(localStorage.getItem('vinculo') || '{}');
  const orgaoInfo = JSON.parse(localStorage.getItem('orgaoInfo') || '{}');
  const isOrgaoUser = usuario?.tipoUsuario === 'ORGAO';
  const anoBase = Number(localStorage.getItem('anoBase')) || new Date().getFullYear();

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API_BASE_URL.DEMANDA}/all`, { headers })
      .then(res => {
        let dados = res.data.filter(d => d.anoBase === anoBase);
        if (isOrgaoUser && usuario?.cpf) {
          dados = dados.filter(d => d.responsavelCpf === usuario.cpf);
        } else if (vinculo?.tipo !== 'ADMIN' && usuario?.cpf) {
          dados = dados.filter(d => d.responsavelCpf === usuario.cpf || d.criadoPorCpf === usuario.cpf);
        }
        setDemandas(dados);
      })
      .catch(() => setDemandas([]));
    axios.get(`${API_BASE_URL.USUARIO}/all`, { headers })
      .then(res => setUsuarios(res.data))
      .catch(() => {});
  }, []);

  const getNomeUsuario = cpf => {
    const u = usuarios.find(usr => usr.cpf === cpf);
    return u ? u.nome : cpf || '-';
  };

  const getDiasDoMes = (mes, ano) => new Date(ano, mes + 1, 0).getDate();
  const getPrimeiroDia = (mes, ano) => new Date(ano, mes, 1).getDay();

  const getDemandasCriadasNoDia = (dia) => {
    return demandas.filter(d => {
      const dt = d.dataCriacao ? new Date(d.dataCriacao) : null;
      return dt && dt.getDate() === dia && dt.getMonth() === mesAtual && dt.getFullYear() === anoBase;
    });
  };

  const getDemandasComPrazoNoDia = (dia) => {
    return demandas.filter(d => {
      const dtPrazo = d.dataPrazo ? new Date(d.dataPrazo + 'T00:00:00') : null;
      return dtPrazo && dtPrazo.getDate() === dia && dtPrazo.getMonth() === mesAtual && dtPrazo.getFullYear() === anoBase;
    });
  };

  const selecionarDia = (dia) => {
    setDiaSelecionado(dia);
    setDemandasDoDia({
      criadas: getDemandasCriadasNoDia(dia),
      prazos: getDemandasComPrazoNoDia(dia),
    });
  };

  const getCorDia = (qtd) => {
    if (qtd === 0) return {};
    if (qtd <= 2) return { background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' };
    if (qtd <= 5) return { background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.4)' };
    return { background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' };
  };

  const getStatusBadge = status => {
    const badges = { ABERTA: 'bg-primary', EM_ANDAMENTO: 'bg-warning text-dark', CONCLUIDA: 'bg-success', CANCELADA: 'bg-secondary' };
    return badges[status] || 'bg-secondary';
  };

  const mesAnterior = () => {
    if (mesAtual > 0) {
      setMesAtual(mesAtual - 1);
      setDiaSelecionado(null);
      setDemandasDoDia({ criadas: [], prazos: [] });
    }
  };

  const proximoMes = () => {
    if (mesAtual < 11) {
      setMesAtual(mesAtual + 1);
      setDiaSelecionado(null);
      setDemandasDoDia({ criadas: [], prazos: [] });
    }
  };

  const abrirDemanda = (d) => {
    setDemandaVisualizar(d);
    setShowDemandaModal(true);
  };

  const diasNoMes = getDiasDoMes(mesAtual, anoBase);
  const primeiroDia = getPrimeiroDia(mesAtual, anoBase);
  const totalDemandasMes = demandas.filter(d => {
    const dt = d.dataCriacao ? new Date(d.dataCriacao) : null;
    return dt && dt.getMonth() === mesAtual && dt.getFullYear() === anoBase;
  }).length;
  const totalConcluidasMes = demandas.filter(d => {
    const dt = d.dataCriacao ? new Date(d.dataCriacao) : null;
    return dt && dt.getMonth() === mesAtual && dt.getFullYear() === anoBase && d.status === 'CONCLUIDA';
  }).length;

  const celulasCalendario = [];
  for (let i = 0; i < primeiroDia; i++) celulasCalendario.push(null);
  for (let d = 1; d <= diasNoMes; d++) celulasCalendario.push(d);

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Painel" icon="bi-house-door" showCloseButton={false} />
        <PageContent style={isDesktop ? { overflow: 'hidden' } : {}}>
          <div className="row g-3" style={isDesktop ? { flex: '1 1 0', minHeight: 0 } : {}}>
            <div className="col-lg-9" style={isDesktop ? { minHeight: 0, display: 'flex', flexDirection: 'column' } : {}}>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '0.5rem' }}>
                    <div className="card-body text-center py-3">
                      <small className="text-muted">Total no mês</small>
                      <h3 className="mb-0 mt-1" style={{ color: 'rgb(55,48,163)' }}>{totalDemandasMes}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '0.5rem' }}>
                    <div className="card-body text-center py-3">
                      <small className="text-muted">Concluídas</small>
                      <h3 className="mb-0 mt-1 text-success">{totalConcluidasMes}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card border-0 shadow-sm" style={{ borderRadius: '0.5rem' }}>
                    <div className="card-body text-center py-3">
                      <small className="text-muted">Pendentes</small>
                      <h3 className="mb-0 mt-1 text-warning">{totalDemandasMes - totalConcluidasMes}</h3>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card border-0 shadow-sm" style={{ borderRadius: '0.5rem', ...(isDesktop ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}) }}>
                <div className="card-header d-flex align-items-center justify-content-between py-2" style={{ background: 'linear-gradient(45deg, rgb(30,27,75) 0%, rgb(55,48,163) 100%)', color: '#fff', borderRadius: '0.5rem 0.5rem 0 0', ...(isDesktop ? { flexShrink: 0 } : {}) }}>
                  <button className="btn btn-sm btn-outline-light py-0 px-2" onClick={mesAnterior} disabled={mesAtual === 0}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  <h6 className="mb-0" style={{ fontSize: '0.9rem' }}>{MONTHS_PT[mesAtual]}</h6>
                  <button className="btn btn-sm btn-outline-light py-0 px-2" onClick={proximoMes} disabled={mesAtual === 11}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
                <div className="card-body p-2" style={isDesktop ? { flex: '1 1 0', minHeight: 0, overflow: 'hidden' } : {}}>
                  <div className="d-flex mb-1">
                    {DIAS_PT.map(d => (
                      <div key={d} className="flex-fill text-center">
                        <small className="fw-bold text-muted" style={{ fontSize: '0.7rem' }}>{d}</small>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                    {celulasCalendario.map((dia, idx) => {
                      if (dia === null) return <div key={`empty-${idx}`}></div>;
                      const qtdCriadas = getDemandasCriadasNoDia(dia).length;
                      const qtdPrazos = getDemandasComPrazoNoDia(dia).length;
                      const qtd = qtdCriadas + qtdPrazos;
                      const ehHoje = dia === new Date().getDate() && mesAtual === new Date().getMonth() && anoBase === new Date().getFullYear();
                      const selecionado = diaSelecionado === dia;
                      const estilosDia = getCorDia(qtd);
                      return (
                        <div
                          key={dia}
                          onClick={() => selecionarDia(dia)}
                          className="text-center"
                          style={{
                            cursor: 'pointer',
                            borderRadius: '0.35rem',
                            fontSize: '0.8rem',
                            padding: '6px 2px',
                            fontWeight: ehHoje ? '700' : '400',
                            border: selecionado ? '2px solid rgb(55,48,163)' : (estilosDia.border || '1px solid transparent'),
                            background: selecionado ? 'rgba(55,48,163,0.1)' : (estilosDia.background || 'transparent'),
                            transition: 'all 0.15s',
                            minHeight: '42px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <div>{dia}</div>
                          {(qtdCriadas > 0 || qtdPrazos > 0) && (
                            <div className="d-flex gap-1 justify-content-center" style={{ fontSize: '0.55rem' }}>
                              {qtdCriadas > 0 && (
                                <span className="badge bg-primary" title={`${qtdCriadas} criada(s)`}>{qtdCriadas}c</span>
                              )}
                              {qtdPrazos > 0 && (
                                <span className="badge bg-warning text-dark" title={`${qtdPrazos} prazo(s)`}>{qtdPrazos}p</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-3" style={isDesktop ? { minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } : {}}>
              <div className="card border-0 shadow-sm" style={{ borderRadius: '0.5rem', background: 'rgba(55,48,163,0.03)', ...(isDesktop ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}) }}>
                <div className="card-header py-2" style={{ background: '#f8f9fa', borderRadius: '0.5rem 0.5rem 0 0' }}>
                  <small className="fw-bold">
                    {diaSelecionado
                      ? `${diaSelecionado} de ${MONTHS_PT[mesAtual]}`
                      : 'Selecione um dia'}
                  </small>
                </div>
                <div className="card-body p-2" style={isDesktop ? { overflowY: 'auto', flex: '1 1 0', minHeight: 0 } : {}}>
                  {!diaSelecionado && (
                    <div className="text-center text-muted py-4">
                      <i className="bi bi-calendar3" style={{ fontSize: '2rem' }}></i>
                      <p className="mt-2" style={{ fontSize: '0.8rem' }}>Clique em um dia no calendário</p>
                    </div>
                  )}
                  {diaSelecionado && demandasDoDia.criadas.length === 0 && demandasDoDia.prazos.length === 0 && (
                    <div className="text-center text-muted py-4">
                      <i className="bi bi-inbox" style={{ fontSize: '1.5rem' }}></i>
                      <p className="mt-2" style={{ fontSize: '0.8rem' }}>Nenhuma demanda neste dia</p>
                    </div>
                  )}
                  {demandasDoDia.criadas.length > 0 && (
                    <div className="mb-2">
                      <small className="fw-bold text-primary d-block mb-1" style={{ fontSize: '0.7rem' }}>
                        <i className="bi bi-plus-circle me-1"></i>Criadas ({demandasDoDia.criadas.length})
                      </small>
                      {demandasDoDia.criadas.map(d => (
                        <div
                          key={`c-${d.id}`}
                          className="p-2 mb-1"
                          style={{ borderRadius: '0.35rem', border: '1px solid rgba(59,130,246,0.2)', background: 'rgba(59,130,246,0.03)', cursor: 'pointer', fontSize: '0.8rem' }}
                          onClick={() => abrirDemanda(d)}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="fw-semibold">{d.titulo}</span>
                            <span className={`badge ${getStatusBadge(d.status)}`} style={{ fontSize: '0.65rem' }}>
                              {d.status?.replace('_', ' ')}
                            </span>
                          </div>
                          <small className="text-muted">
                            <i className="bi bi-person me-1"></i>{getNomeUsuario(d.responsavelCpf)}
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
                  {demandasDoDia.prazos.length > 0 && (
                    <div className="mb-2">
                      <small className="fw-bold text-warning d-block mb-1" style={{ fontSize: '0.7rem' }}>
                        <i className="bi bi-calendar-event me-1"></i>Prazo ({demandasDoDia.prazos.length})
                      </small>
                      {demandasDoDia.prazos.map(d => (
                        <div
                          key={`p-${d.id}`}
                          className="p-2 mb-1"
                          style={{ borderRadius: '0.35rem', border: '1px solid rgba(234,179,8,0.3)', background: 'rgba(234,179,8,0.05)', cursor: 'pointer', fontSize: '0.8rem' }}
                          onClick={() => abrirDemanda(d)}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <span className="fw-semibold">{d.titulo}</span>
                            <span className={`badge ${getStatusBadge(d.status)}`} style={{ fontSize: '0.65rem' }}>
                              {d.status?.replace('_', ' ')}
                            </span>
                          </div>
                          <small className="text-muted">
                            <i className="bi bi-person me-1"></i>{getNomeUsuario(d.responsavelCpf)}
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </PageContent>
      </ResponsiveShadow>
      {showDemandaModal && (
        <DemandaModal
          show={showDemandaModal}
          onClose={() => { setShowDemandaModal(false); setDemandaVisualizar(null); }}
          onSuccess={() => { setShowDemandaModal(false); setDemandaVisualizar(null); }}
          demanda={demandaVisualizar}
          usuarios={usuarios}
          readOnly={true}
        />
      )}
    </Layout>
  );
}

export default Home;
